import { useEffect } from 'react';
import { View, ActivityIndicator, Platform } from 'react-native';
import * as Linking from 'expo-linking';
import { getSupabaseClient } from '@/lib/supabase/supabase';
import { useAuthStore } from '@/store/authStore';
import { router } from 'expo-router';
import { ThemedText } from '@/components/themed-text';
import {
  createSessionFromOAuthUrl,
  isOAuthCallbackUrl,
} from '@/lib/oauth/google-oauth';
import { isAppHandoffCallbackUrl, parseHandoffTokenFromUrl } from '@/lib/auth/app-handoff';

/**
 * OAuth, handoff web→app ou magic link em controlapagui://auth/callback.
 * Redefinição de senha NÃO passa por aqui: o link do email aponta direto para
 * /reset-password?token=... e é validado pela nossa API.
 */

export default function AuthCallback() {
  const { loginWithOAuthToken, loginWithHandoff } = useAuthStore();

  useEffect(() => {
    let isMounted = true;

    const finishWithSession = async (accessToken: string) => {
      try {
        await loginWithOAuthToken(accessToken);
      } catch (err) {
        console.error('Backend login failed:', err);
        if (isMounted) router.replace('/(auth)/login');
      }
    };

    const tryHandoff = async (url: string) => {
      const handoff = parseHandoffTokenFromUrl(url);
      if (!handoff) return false;
      try {
        await loginWithHandoff(handoff);
        return true;
      } catch {
        if (isMounted) router.replace('/(auth)/login');
        return true;
      }
    };

    const checkSession = async () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const href = window.location.href;
        if (await tryHandoff(href)) return;
        if (isOAuthCallbackUrl(href)) {
          try {
            const session = await createSessionFromOAuthUrl(href);
            if (session?.access_token) {
              await finishWithSession(session.access_token);
              return;
            }
          } catch (error) {
            console.error('Web OAuth callback error:', error);
            router.replace('/(auth)/login');
            return;
          }
        }
      }

      if (Platform.OS !== 'web') {
        const initialUrl = await Linking.getInitialURL();
        if (initialUrl && isAppHandoffCallbackUrl(initialUrl)) {
          await tryHandoff(initialUrl);
          return;
        }
        if (initialUrl && isOAuthCallbackUrl(initialUrl)) {
          try {
            const session = await createSessionFromOAuthUrl(initialUrl);
            if (session?.access_token) {
              await finishWithSession(session.access_token);
              return;
            }
          } catch (error) {
            console.error('Native OAuth callback error:', error);
            router.replace('/(auth)/login');
            return;
          }
        }
      }

      const supabase = getSupabaseClient();
      if (!supabase) {
        router.replace('/(auth)/login');
        return;
      }

      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Session error:', error);
        router.replace('/(auth)/login');
        return;
      }

      if (session?.access_token) {
        await finishWithSession(session.access_token);
        return;
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
        if (!isMounted) return;

        if (event === 'SIGNED_IN' && nextSession?.access_token) {
          subscription.unsubscribe();
          await finishWithSession(nextSession.access_token);
        }
      });

      setTimeout(() => {
        if (isMounted) {
          subscription.unsubscribe();
          router.replace('/(auth)/login');
        }
      }, 10000);
    };

    checkSession();

    return () => {
      isMounted = false;
    };
  }, [loginWithOAuthToken, loginWithHandoff]);

  return (
    <View className="flex-1 items-center justify-center bg-background">
      <ActivityIndicator size="large" color="#FD5001" />
      <ThemedText className="mt-4 text-center">Finalizando login...</ThemedText>
    </View>
  );
}
